import type { BootstrapBundle } from "./bootstrap-bundle-types.js";

export interface PollUntilBoundOptions {
  apiUrl: string;
  bootstrapToken: string;
  pollIntervalMs?: number;
  maxWaitMs?: number;
}

/** Thrown by `pollUntilBound` on a non-200/204 response; carries the HTTP
 * status so callers can distinguish transient conditions (e.g. 429) from
 * fatal ones (e.g. 401/403) without string-matching the message. */
export class PollUntilBoundHttpError extends Error {
  constructor(public readonly status: number) {
    super(`pollUntilBound got unexpected status ${status}`);
    this.name = "PollUntilBoundHttpError";
  }
}

async function sleep(ms: number): Promise<void> {
  await new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

export async function pollUntilBound(opts: PollUntilBoundOptions): Promise<BootstrapBundle> {
  const pollIntervalMs = opts.pollIntervalMs ?? 2_000;
  const maxWaitMs = opts.maxWaitMs ?? 30 * 60 * 1_000;
  const deadline = Date.now() + maxWaitMs;

  while (true) {
    const response = await fetch(`${opts.apiUrl}/api/v3/pods/bootstrap`, {
      headers: { Authorization: `Bearer ${opts.bootstrapToken}` },
    });

    if (response.status === 200) {
      return (await response.json()) as BootstrapBundle;
    }
    if (response.status === 204) {
      if (Date.now() >= deadline) {
        throw new Error(`pollUntilBound timed out after ${maxWaitMs}ms waiting for pod bind`);
      }
      await sleep(pollIntervalMs);
      continue;
    }
    throw new PollUntilBoundHttpError(response.status);
  }
}
